import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useToast } from "../../context/ToastContext";
import API from "../../api/axiosConfig";
import { useEffect, useState, useCallback } from "react";

export default function HoursAddPage() {
  const showToast = useToast();

  // Variables for invoice
  const [hours, setHours] = useState("");
  const [overtimeHours, setOvertimeHours] = useState("");
  const [pastSubmissions, setPastSubmissions] = useState([]); // to display user's prior entries

  // --- UI/UX State ---
  const [loading, setLoading] = useState(false);

  // Function that calls backend to fetch user's prior entries
  const fetchUserLines = useCallback(async () => {
    setLoading(true);
    // Post request to backend to get lines for this user
    try {
      const response = await API.post("/api/invoice/getUserLines");
      setPastSubmissions(response.data);
    } catch (err) {
      showToast("Error fetching user history", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);
  
  useEffect(() => {

    fetchUserLines();
  }, [fetchUserLines]); //only run at startup

  // Ensure only numbers get put into hours slot before setting it
  const handleHoursChange = (value) => {
    // Regex: Only allow digits and one decimal point
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setHours(value);
    }
  };
  const handleOvertimeHoursChange = (value) => {
    // Regex: Only allow digits and one decimal point
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setOvertimeHours(value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      // Make the POST request to the backend
      await API.post("/api/invoice/hours", {
        hours: hours,
        overtimeHours: overtimeHours || 0,
      });
      setHours("");
      setOvertimeHours("");
      showToast("Hours added to invoice!", "success");
    } catch (err) {
      showToast("Error adding hours! " + err.response.data.message, "error");
    }
    fetchUserLines();
    setLoading(false);
  };

  return (
    <div>
      <h1>Track Hours for Invoice</h1>
      <Paper
        elevation={6}
        sx={{
          padding: "16px",
          margin: "16px auto",
          width: 900,
        }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <TextField
            label="Hours"
            variant="outlined"
            type="number"
            value={hours}
            onChange={(e) => handleHoursChange(e.target.value)}
            sx={{ marginBottom: "16px" }}
            required
          />

          <TextField
            label="Overtime Hours"
            variant="outlined"
            type="number"
            value={overtimeHours}
            onChange={(e) => handleOvertimeHoursChange(e.target.value)}
            sx={{ marginBottom: "16px" }}
          />

          <Button
            variant="contained"
            sx={{
              bgcolor: "darkred",
              "&:hover": {
                bgcolor: "red",
              },
            }}
            disabled={loading}
            type="submit"
          >
            {loading ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </Box>
        <Divider sx={{ my: 3 }}>
          <Typography color="textSecondary" variant="h6">
            Past Submissions
          </Typography>
        </Divider>

        {/* ---  HISTORY TABLE SECTION --- */}
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Employee</strong>
                </TableCell>
                <TableCell>
                  <strong>Invoice Date</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Hours</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Overtime</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pastSubmissions.length > 0 ? (
                pastSubmissions.map((row) => (
                  <TableRow key={row.invoiceId} hover>
                    <TableCell>{row.employeeName}</TableCell>
                    <TableCell>{row.invoiceDate}</TableCell>
                    <TableCell align="right">{row.hours}</TableCell>
                    <TableCell align="right">{row.overtimeHours}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    No past submissions found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
